import { Target, Shield, Sparkles, Zap, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";


const features = [
  {
    icon: Target,
    title: "Manual Link Outreach",
    description: "We reach out to real website owners and editors in your industry. Each email is personalized, each placement is vetted for quality. No automation, no templates, just genuine relationship building that leads to sustainable backlinks from authoritative sources.",
    gradient: "from-blue-500/20 to-cyan-500/20",
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-500",
  },
  {
    icon: Shield,
    title: "Integrity First",
    description: "Every single link we build goes through a rigorous vetting process. We check Domain Rating, organic traffic, spam scores, and content quality. Our transparent reporting shows exactly where your links are placed and why they matter for your SEO strategy.",
    gradient: "from-emerald-500/20 to-green-500/20",
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-500",
  },
  {
    icon: Sparkles,
    title: "Excellence Delivered",
    description: "From high-authority guest posts to strategic niche edits, we prioritize quality over quantity. Our dedicated account managers ensure responsive communication, on-time delivery, and placements that genuinely move the needle for your search rankings.",
    gradient: "from-accent/20 to-orange-500/20",
    iconBg: "bg-accent/10",
    iconColor: "text-accent",
  },
];

const QualitativeApproach = () => {
  return (
    <section className="py-20 md:py-32 bg-background relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Our Approach</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
            <span className="text-primary">Our Link Building</span>{" "}
            <span className="bg-gradient-to-r from-accent to-orange-500 bg-clip-text text-transparent pb-2 inline-block" style={{ lineHeight: '1.2' }}>
              Strategy
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            We only build links from real websites with real organic traffic. No shortcuts, no spam, just sustainable results that stand the test of time.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
          {features.map((feature, index) => {
            const content = (
              <>
                {/* Gradient overlay on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl`} />
                
                <div className="relative z-10">
                  <div className={`w-16 h-16 md:w-20 md:h-20 ${feature.iconBg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className={`w-8 h-8 md:w-10 md:h-10 ${feature.iconColor}`} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-foreground mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {feature.description}
                  </p>
                </div>

                {/* Decorative corner */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary/10 to-transparent rounded-tr-3xl rounded-bl-full opacity-50" />
              </>
            );

            return (
              <div key={index} className="group relative bg-card rounded-3xl p-8 md:p-10 border border-border/50 hover:border-primary/30 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2">
                {content}
              </div>
            );
          })}
        </div>

        <div className="text-center mt-16 md:mt-20 max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-3xl p-8 md:p-12 border border-primary/20 overflow-hidden">
            {/* Animated gradient border effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-[shimmer_3s_infinite]" style={{ animationName: 'shimmer' }} />
            
            <p className="text-xl md:text-2xl text-foreground font-semibold mb-3">
              SEO has evolved beyond simple tactics
            </p>
            <p className="text-muted-foreground text-lg mb-6">
              Success today is built on trust, authority, and brand equity, not just links. Our comprehensive approach ensures your rankings are built to last.
            </p>
            <Link
              to="/b2b-link-building"
              className="inline-flex items-center text-accent font-semibold hover:underline"
            >
              See our B2B link building approach <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QualitativeApproach;
